import {
  type BasePassage,
  type Hashtag,
  type Passage,
  type SetHtRequest,
  type SyncedHashtag,
  type SyncedPassage,
} from '@soulhx/fs-common';
import { v4 as uuid } from 'uuid';
import { db, type ItemSyncStatus, type LocalPassage, type LocalPassageHashtag } from '../model/db.js';
import { triggerGlobalSync } from './GlobalSyncMiddleware.js';
import { isItemGoneError, syncFetch, syncPull } from './syncFetch.js';
import { nextSyncCursor, nowUtcIso, pullSince, toUtcIso } from './timestamps.js';

export const PASSAGE_SYNC_POLL_INTERVAL_MS = 20_000;
export const PASSAGE_SYNC_KEY = 'passages';

const PASSAGES_PATH = '/read/p';
const HASHTAG_SYNC_CURSOR_KEY = 'passageHashtags';

// see SettingSync.ts - passing `since` at all (even this far back) routes
// the server to the sync-oriented delta endpoint on the very first pull
const EPOCH = new Date(0).toISOString();

const isUnsynced = (status: ItemSyncStatus | undefined): boolean => status !== undefined && status !== 'synced';

const logSyncError = (context: string, error: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`passage sync: ${context}`, error);
};

/**
 * Resolves hashtag ids (e.g. from a `LocalPassage.hashtagIds`) against the
 * locally-synced hashtag catalog. Any id not yet known locally is silently
 * dropped rather than thrown - callers render whatever resolves.
 */
export const resolvePassageHashtagIds = async (ids: string[]): Promise<Hashtag[]> => {
  if (ids.length === 0) return [];

  const rows = await db.passageHashtags.bulkGet(ids);

  return rows
    .filter((row): row is LocalPassageHashtag => row !== undefined)
    .map((row) => ({ id: row.id, tag: row.tag }));
};

/**
 * Resolves a list of tag text to hashtag ids against the local catalog,
 * creating a local placeholder row (client-generated id, `pendingCreate`)
 * for any tag text not already known - lets a brand-new tag be attached to
 * a passage fully offline. The placeholder isn't independently pushed; it's
 * resolved (and reconciled to a canonical id if the server disagrees) as
 * part of the owning passage's tag-push step - see `pushPassageTags`.
 */
const resolveOrCreateLocalPassageHashtagIds = async (tags: string[]): Promise<string[]> => {
  const fixedTags = tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
  const ids: string[] = [];

  await db.transaction('rw', db.passageHashtags, async () => {
    for (const tag of fixedTags) {
      const existing = await db.passageHashtags.filter((row) => row.tag === tag).first();

      if (existing) {
        ids.push(existing.id);
        continue;
      }

      const placeholder: LocalPassageHashtag = { id: uuid(), tag, syncStatus: 'pendingCreate' };
      await db.passageHashtags.add(placeholder);
      ids.push(placeholder.id);
    }
  });

  return ids;
};

/**
 * Pushes a passage's full desired tag list via the existing `setTags`
 * endpoint (find-or-create + full diff, all in one call) rather than
 * per-tag add/remove - simpler, and lets the server resolve any brand-new
 * tag to a canonical id in the same round trip. The server doesn't accept a
 * proposed id on this endpoint, so a locally-generated placeholder id for a
 * new tag is *always* replaced by the server's canonical id here (not just
 * on a race) - reconciled below by upserting the canonical row, repointing
 * any other local passage still referencing the placeholder, and dropping
 * the now-superseded placeholder.
 */
const pushPassageTags = async (local: LocalPassage, keepalive?: boolean): Promise<void> => {
  // Re-read from the DB rather than trusting `local.hashtagIds`: pushPendingPassages
  // takes one snapshot of every pending passage before looping, so an earlier
  // passage in the same sync pass may have already reconciled (and repointed) a
  // placeholder tag id that this passage's stale, in-memory copy still references -
  // see the repoint step below.
  const current = await db.passages.get(local.id);
  const localIds = current?.hashtagIds ?? [];
  const localHashtags = await db.passageHashtags.bulkGet(localIds);
  const tagTexts = localHashtags.filter((row): row is LocalPassageHashtag => row !== undefined).map((row) => row.tag);

  const body: SetHtRequest = { tags: tagTexts };
  const canonical = await syncFetch<Hashtag[]>({
    method: 'POST',
    path: `${PASSAGES_PATH}/${local.id}/setTags`,
    body,
    keepalive,
  });

  await db.transaction('rw', db.passages, db.passageHashtags, async () => {
    const canonicalIds: string[] = [];

    for (const ht of canonical) {
      canonicalIds.push(ht.id);

      const existingRow = await db.passageHashtags.get(ht.id);
      if (!existingRow || !isUnsynced(existingRow.syncStatus)) {
        await db.passageHashtags.put({ id: ht.id, tag: ht.tag, syncStatus: 'synced' });
      }
    }

    // drop any local placeholder that got superseded by a different
    // canonical id for the same tag text - and repoint any OTHER local
    // passage still referencing that placeholder (e.g. several passages
    // tagged offline with the same brand-new tag before any of them
    // synced) onto the canonical id, so it doesn't silently lose the tag
    // when its own push runs, whether later in this same pass or on a
    // future retry
    for (const oldId of localIds) {
      if (canonicalIds.includes(oldId)) continue;

      const oldRow = await db.passageHashtags.get(oldId);
      if (!oldRow) continue;

      const canonicalMatch = canonical.find((ht) => ht.tag === oldRow.tag);
      if (!canonicalMatch) continue;

      const referencing = await db.passages.filter((p) => (p.hashtagIds ?? []).includes(oldId)).toArray();
      for (const p of referencing) {
        const nextIds = Array.from(new Set(p.hashtagIds!.map((id) => (id === oldId ? canonicalMatch.id : id))));
        await db.passages.update(p.id, { hashtagIds: nextIds });
      }

      await db.passageHashtags.delete(oldId);
    }

    await db.passages.update(local.id, {
      hashtagIds: canonicalIds.length > 0 ? canonicalIds : undefined,
      syncStatus: 'synced',
    });
  });
};

const pushCreate = async (local: LocalPassage, keepalive?: boolean): Promise<void> => {
  const body: BasePassage = {
    id: local.id,
    osis: local.osis,
    version: local.version,
    bibleViewOptions: local.bibleViewOptions,
  };
  const serverPassage = await syncFetch<Passage>({ method: 'POST', path: `${PASSAGES_PATH}/`, body, keepalive });

  await db.passages.update(local.id, { updateDate: toUtcIso(serverPassage.updateDate) });
  await pushPassageTags(local, keepalive);
};

const pushUpdate = async (local: LocalPassage, keepalive?: boolean): Promise<void> => {
  const body = { osis: local.osis, version: local.version, bibleViewOptions: local.bibleViewOptions };
  const serverPassage = await syncFetch<Passage>({
    method: 'PUT',
    path: `${PASSAGES_PATH}/${local.id}`,
    body,
    keepalive,
  });

  await db.passages.update(local.id, { updateDate: toUtcIso(serverPassage.updateDate) });
  await pushPassageTags(local, keepalive);
};

const pushDelete = async (local: LocalPassage, keepalive?: boolean): Promise<void> => {
  await syncFetch<{ success: boolean }>({ method: 'DELETE', path: `${PASSAGES_PATH}/${local.id}`, keepalive });
  await db.passages.delete(local.id);
};

/**
 * Drops a pending passage the server reports as gone (see
 * `isItemGoneError`) - e.g. deleted on another device while this edit was
 * pending, or a replayed create of a since-deleted passage whose follow-up
 * setTags then 404s. Without this the row stays pending and fails on every
 * cycle forever, since a pull never overwrites an unsynced row. Also drops
 * any local placeholder hashtag that only this passage referenced, since
 * placeholders are only ever resolved via their passage's tag push.
 */
const dropGonePassage = async (id: string): Promise<void> => {
  await db.transaction('rw', db.passages, db.passageHashtags, async () => {
    const existing = await db.passages.get(id);
    await db.passages.delete(id);

    for (const tagId of existing?.hashtagIds ?? []) {
      const tagRow = await db.passageHashtags.get(tagId);
      if (tagRow?.syncStatus !== 'pendingCreate') continue;

      const stillReferenced = await db.passages.filter((p) => (p.hashtagIds ?? []).includes(tagId)).count();
      if (stillReferenced === 0) await db.passageHashtags.delete(tagId);
    }
  });
};

export const pushPendingPassages = async (options?: { keepalive?: boolean }): Promise<void> => {
  const keepalive = options?.keepalive;
  const pending = await db.passages.filter((p) => isUnsynced(p.syncStatus)).toArray();

  for (const local of pending) {
    try {
      switch (local.syncStatus ?? 'pendingCreate') {
        case 'pendingCreate':
          await pushCreate(local, keepalive);
          break;
        case 'pendingUpdate':
          await pushUpdate(local, keepalive);
          break;
        case 'pendingDelete':
          await pushDelete(local, keepalive);
          break;
      }
    } catch (error) {
      if (isItemGoneError(error)) {
        await dropGonePassage(local.id);
        continue;
      }

      // leave the row's syncStatus untouched so it's retried on the next cycle
      logSyncError(`failed to push passage ${local.id}`, error);
    }
  }
};

export const pullPassageHashtags = async (): Promise<void> => {
  const cursor = await db.syncMeta.get(HASHTAG_SYNC_CURSOR_KEY);
  const since = cursor?.lastSyncedAt ?? EPOCH;
  const { data: serverHashtags, horizon } = await syncPull<SyncedHashtag[]>(
    `${PASSAGES_PATH}/ht/sync?since=${encodeURIComponent(pullSince(since))}`
  );

  let maxLastUpdate = since;

  await db.transaction('rw', db.passageHashtags, db.syncMeta, async () => {
    for (const serverHashtag of serverHashtags) {
      const updateDate = toUtcIso(serverHashtag.updateDate);
      if (updateDate > maxLastUpdate) {
        maxLastUpdate = updateDate;
      }

      if (serverHashtag.deleted) {
        await db.passageHashtags.delete(serverHashtag.id);
        continue;
      }

      const local = await db.passageHashtags.get(serverHashtag.id);
      if (local && isUnsynced(local.syncStatus)) continue;

      if (local?.updateDate === updateDate) continue;

      const toStore: LocalPassageHashtag = {
        id: serverHashtag.id,
        tag: serverHashtag.tag,
        updateDate,
        deleted: false,
        syncStatus: 'synced',
      };
      await db.passageHashtags.put(toStore);
    }

    await db.syncMeta.put({
      itemId: HASHTAG_SYNC_CURSOR_KEY,
      lastSyncedAt: nextSyncCursor(since, maxLastUpdate, horizon),
    });
  });
};

export const pullPassages = async (): Promise<void> => {
  const cursor = await db.syncMeta.get(PASSAGE_SYNC_KEY);
  const since = cursor?.lastSyncedAt ?? EPOCH;
  const { data: serverPassages, horizon } = await syncPull<SyncedPassage[]>(
    `${PASSAGES_PATH}/sync?since=${encodeURIComponent(pullSince(since))}`
  );

  let maxLastUpdate = since;

  await db.transaction('rw', db.passages, db.syncMeta, async () => {
    for (const serverPassage of serverPassages) {
      const updateDate = toUtcIso(serverPassage.updateDate);
      if (updateDate > maxLastUpdate) {
        maxLastUpdate = updateDate;
      }

      if (serverPassage.deleted) {
        await db.passages.delete(serverPassage.id);
        continue;
      }

      const local = await db.passages.get(serverPassage.id);
      if (local && isUnsynced(local.syncStatus)) continue;

      if (local?.updateDate === updateDate) continue;

      const toStore: LocalPassage = { ...serverPassage, updateDate, syncStatus: 'synced' };
      await db.passages.put(toStore);
    }

    await db.syncMeta.put({ itemId: PASSAGE_SYNC_KEY, lastSyncedAt: nextSyncCursor(since, maxLastUpdate, horizon) });
  });
};

/**
 * Pushes any pending local passage mutations, then pulls whatever changed
 * on the server since the last sync - hashtag catalog first, then
 * passages, so a passage referencing a brand-new hashtag id (from another
 * device) resolves against an already-up-to-date local catalog.
 * Push-before-pull so local changes reach `synced` before we ask "what's
 * new", minimizing cases where the pull side has to skip an in-flight
 * local row.
 */
export const syncPassages = async (): Promise<void> => {
  await pushPendingPassages();
  await pullPassageHashtags();
  await pullPassages();
};

export const queueLocalPassageCreate = async (
  osis: string,
  version: string,
  tags?: string[],
  bibleViewOptions?: string
): Promise<string> => {
  const hashtagIds = tags && tags.length > 0 ? await resolveOrCreateLocalPassageHashtagIds(tags) : undefined;

  const localPassage: LocalPassage = {
    id: uuid(),
    osis,
    version,
    updateDate: nowUtcIso(),
    hashtagIds,
    bibleViewOptions,
    syncStatus: 'pendingCreate',
  };

  await db.passages.add(localPassage);
  triggerGlobalSync(PASSAGE_SYNC_KEY);
  return localPassage.id;
};

export const queueLocalPassageUpdate = async (
  id: string,
  changes: { osis?: string; version?: string; tags?: string[]; bibleViewOptions?: string }
): Promise<void> => {
  const updated = await db.transaction('rw', db.passages, db.passageHashtags, async () => {
    const existing = await db.passages.get(id);
    if (!existing || existing.syncStatus === 'pendingDelete') return false;

    const hashtagIds =
      changes.tags !== undefined ? await resolveOrCreateLocalPassageHashtagIds(changes.tags) : existing.hashtagIds;
    const nextStatus: ItemSyncStatus = existing.syncStatus === 'pendingCreate' ? 'pendingCreate' : 'pendingUpdate';

    await db.passages.update(id, {
      osis: changes.osis ?? existing.osis,
      version: changes.version ?? existing.version,
      hashtagIds,
      bibleViewOptions: changes.bibleViewOptions ?? existing.bibleViewOptions,
      updateDate: nowUtcIso(),
      syncStatus: nextStatus,
    });
    return true;
  });

  if (updated) triggerGlobalSync(PASSAGE_SYNC_KEY);
};

export const queueLocalPassageDelete = async (id: string): Promise<void> => {
  const shouldSync = await db.transaction('rw', db.passages, async () => {
    const existing = await db.passages.get(id);
    if (!existing) return false;

    if (existing.syncStatus === undefined || existing.syncStatus === 'pendingCreate') {
      // never reached the server - just vanish, no network call
      await db.passages.delete(id);
      return false;
    }

    if (existing.syncStatus === 'pendingDelete') return false;

    await db.passages.update(id, { syncStatus: 'pendingDelete' });
    return true;
  });

  if (shouldSync) triggerGlobalSync(PASSAGE_SYNC_KEY);
};
